import { z } from 'zod';
import { personNameSchema } from './person-name.js';
import { UserRoleSchema } from '../enums/user-role.js';
import { EmploymentStatusSchema } from '../enums/employment-status.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';
import { MATRICULE_FULL_REGEX } from './matricule.js';
import { AuthLevelSchema } from './pin.js';

export const UserSchema = z.object({
  id: UuidV7Schema,
  clerkId: z.string(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: UserRoleSchema,
  partnerId: UuidV7Schema,
  /**
   * Matricule Doorly — format `XXX-NNNNNN-Y`, scope partner, figé à la
   * création. Cf. `schemas/matricule.ts`. Always present (DB NOT NULL).
   */
  matricule: z.string().regex(MATRICULE_FULL_REGEX),
  managerId: UuidV7Schema.nullable(),
  superviseurId: UuidV7Schema.nullable(),
  phone: z.string().nullable().optional(),
  externalId: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  /**
   * Chaîne de responsabilité (cf. `EmploymentStatusSchema`). `null` sur les
   * comptes créés avant la typologie : à compléter par le RP, rien n'est
   * bloqué tant que la personne a une adresse email.
   */
  employmentStatus: EmploymentStatusSchema.nullable().optional(),
  archived: z.boolean(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
  /**
   * Compteur lecture-seule retourné par `GET /users` : nombre de leads
   * créés par ce vendeur sur les 7 derniers jours (rolling). Sert au
   * badge d'activité affiché dans la liste — un vendeur < 5 leads/7j
   * est considéré "peu actif". Optionnel parce que non pertinent pour
   * les rôles non vendeurs (l'API peut renvoyer 0 ou omettre).
   */
  leads7d: z.number().int().min(0).optional(),
  /**
   * Le vendeur a posé un code PIN (tablette partagée). Le hash lui-même ne
   * sort jamais de l'API — seul ce booléen circule.
   */
  hasPin: z.boolean().optional(),
  /**
   * Renseignés par `GET /users/me` uniquement : niveau d'authentification de
   * la session courante et, en session PIN, le superviseur dont la session
   * porte la tablette (cf. `AuthenticatedUser` côté API).
   */
  authLevel: AuthLevelSchema.optional(),
  deviceUserId: UuidV7Schema.nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

const CreateUserBaseSchema = z.object({
  // Casse « Prénom Nom » à l'entrée (cf. `person-name.ts`).
  firstName: personNameSchema({ min: 1, max: 100 }),
  lastName: personNameSchema({ min: 1, max: 100 }),
  role: UserRoleSchema,
  partnerId: UuidV7Schema,
  // « Aucun » d'un select = chaîne vide → null (comme `CreateInvitationSchema`) :
  // sans ça, retirer le superviseur d'un vendeur était refusé (UUID invalide).
  managerId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
  superviseurId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
});

export const CreateUserSchema = CreateUserBaseSchema.extend({
  email: z.string().email(),
  /** Obligatoire à la création depuis la typologie (15/09/2026). */
  employmentStatus: EmploymentStatusSchema,
});
export type CreateUserInput = z.infer<typeof CreateUserSchema>;

/**
 * Sous-domaine des adresses de service attribuées aux comptes sans email
 * réel. **Aucun MX ne doit jamais être publié dessus** : rien n'y est
 * délivrable, donc personne ne peut croire disposer d'un canal de
 * récupération sur ces comptes — le reset passe obligatoirement par le
 * manager.
 *
 * Contrairement aux domaines fictifs déjà présents dans le code
 * (`@invitation.doorly` des invitations temporaires, `@anonymized.doorly` de
 * l'anonymisation admin), celui-ci est envoyé à Clerk, qui refuse en 422 un
 * TLD inexistant. D'où un vrai sous-domaine d'un domaine qu'on possède.
 */
export const CREDENTIAL_EMAIL_DOMAIN = 'vendeur.doorly.pro';
export const CREDENTIAL_EMAIL_REGEX = /@vendeur\.doorly\.pro$/i;

/**
 * `true` si ce compte n'a pas d'adresse email réelle — donc pas de « mot de
 * passe oublié », pas de code de connexion par mail, et aucune façon de
 * prouver une identité en dehors de son manager.
 *
 * Sert de garde-fou côté API : un compte credential-only ne peut **jamais**
 * en créer un autre. La chaîne de responsabilité doit toujours remonter à
 * quelqu'un joignable par mail, puisque c'est sur lui que repose l'engagement
 * contractuel du partenaire.
 */
export function isCredentialOnlyEmail(email: string | null | undefined): boolean {
  return !!email && CREDENTIAL_EMAIL_REGEX.test(email);
}

/**
 * Création d'un utilisateur **sans adresse email**. L'identifiant de
 * connexion et l'adresse de service sont dérivés du matricule alloué à la
 * création — le client ne les choisit pas, il les reçoit en retour.
 */
/**
 * Seuls rôles créables sans adresse email.
 *
 * C'est le besoin réel : les réseaux de boutiques disposent d'une adresse
 * par magasin — donc managers et responsables peuvent s'en servir — mais
 * d'aucune adresse par vendeur, et refusent d'en créer.
 *
 * Au-dessus du superviseur, un compte sans email est de toute façon un
 * cul-de-sac : il ne peut inviter personne (`assertCanInviteInPartner`), or
 * administrer son réseau est justement l'essentiel du travail d'un RP. Et un
 * admin sans email n'aurait aucun recours pour récupérer son accès — ni
 * « mot de passe oublié », ni code par mail, ni personne au-dessus de lui.
 */
export const CREDENTIAL_CREATABLE_ROLES = ['superviseur', 'vendeur'] as const;
export type CredentialCreatableRole = (typeof CREDENTIAL_CREATABLE_ROLES)[number];

export const CreateCredentialUserSchema = CreateUserBaseSchema.extend({
  role: z.enum(CREDENTIAL_CREATABLE_ROLES),
  /**
   * Un compte sans adresse email n'est ouvert qu'à un **salarié** : il est
   * sous la responsabilité directe de son manager, qui répond de l'accès.
   * Un non-salarié passe par l'invitation email, sans exception.
   */
  employmentStatus: z.literal('employee'),
  /**
   * Matricule externe côté partenaire (ou nom/prénom déjà présents). Le
   * responsable du magasin déclare qui est derrière l'identifiant : c'est la
   * trace de correspondance exigée par le contrat, sans laquelle on ne sait
   * plus qui a accédé aux données client.
   */
  externalId: z.string().min(1).max(50),
  /**
   * Optionnel comme sur le chemin email. Ce n'est PAS un canal
   * d'authentification — les réseaux de boutiques refusent de partager les
   * numéros de leurs vendeurs, et une adresse de service ne doit jamais avoir
   * de second facteur exploitable. C'est un moyen de contact pour la
   * hiérarchie, rien de plus.
   */
  phone: z.string().max(30).nullable().optional(),
  /**
   * Certification explicite par l'inviteur que la personne appartient bien à
   * l'organisation sous contrat. Doit valoir `true` — c'est ce qui matérialise
   * le transfert de responsabilité.
   */
  certifiedByInviter: z.literal(true),
});
export type CreateCredentialUserInput = z.infer<typeof CreateCredentialUserSchema>;

export const UpdateUserSchema = CreateUserSchema.partial().extend({
  phone: z.string().max(30).nullable().optional(),
  externalId: z.string().max(50).nullable().optional(),
  bio: z.string().max(500).nullable().optional(),
  archived: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;
